import { useQuery } from '@tanstack/react-query';
import { getConfig } from '../../config/runtimeConfig';
import { HttpError } from '../../lib/httpError';
import type { ServerVisibility, StudyGroup } from '../../features/collections/api';
import type { ImageType } from '../../data/imageTypes';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { fixtureCover } from '../dev/fixtures';

/**
 * Explore, the public collection page, search and Home. Guests read the
 * /public namespace; signed-in people the authenticated routes (which add
 * their likes and "own" flags). Same shapes either way.
 *
 * Studies are named by public id (`stu_…`) and images by theirs (`img_…`):
 * a DICOM UID is unique only inside a workspace. Anything read from outside a
 * study's workspace (Explore, a public collection) lists only studies with a
 * PUBLISHED image, and counts only those images.
 */
export interface ExploreCard {
  public_id: string;
  name: string;
  description: string | null;
  visibility: ServerVisibility;
  author: string | null;
  organ: string | null;
  studies: number;
  groups: number;
  /** Study public ids. */
  covers: string[];
  like_count: number;
  comment_count: number;
  liked: boolean;
  recent_likes: number;
  created_utc: string;
  own: boolean;
}

export interface ExploreStudy {
  public_id: string;
  study_id: string | null;
  description: string | null;
  images: number;
  like_count: number;
  comment_count: number;
  liked: boolean;
  /** null when the collection is private: the study stands on its own */
  collection_id: string | null;
  collection_name: string | null;
  author: string | null;
  created_utc: string;
}

export interface ExploreData {
  collections: ExploreCard[];
  organs: string[];
  studies: ExploreStudy[];
}

export interface PublicStudy {
  public_id: string;
  study_id: string | null;
  description: string | null;
  diagnosis: string | null;
  visibility: ServerVisibility;
  visibility_own: ServerVisibility;
  like_count: number;
  comment_count: number;
  liked: boolean;
  images: number;
  group_public_id: string | null;
  created_utc: string;
}

export interface PublicCollection extends Omit<ExploreCard, 'studies' | 'groups'> {
  groups: StudyGroup[];
  studies: PublicStudy[];
}

export interface SearchResults {
  collections: { items: Array<{ public_id: string; name: string; description: string | null; studies: number; cover: string | null }>; total: number };
  groups: { items: Array<{ public_id: string; name: string; collection_id: string; collection_name: string; position: number; studies: number }>; total: number };
  studies: { items: Array<{ public_id: string; study_id: string | null; description: string | null; collection_id: string | null; collection_name: string | null; group_name: string | null; images: number }>; total: number };
  /** `study` is the study's public id. */
  images: { items: Array<{ image_id: string; study: string; series_instance_uid: string; original_filename: string | null; description: string | null; stain_meaning: string | null; kind: 'wsi' | 'flat' | 'document'; image_type: ImageType | null; study_id: string | null; collection_id: string | null; collection_name: string | null }>; total: number };
}

export interface HomeData {
  workspace: string;
  collectionCount: number;
  recentCollections: Array<{ public_id: string; name: string; description: string | null; visibility: ServerVisibility; author: string | null; like_count: number; comment_count: number; liked: boolean; studies: number; covers: string[] }>;
  recentStudies: Array<{ public_id: string; study_id: string | null; description: string | null; visibility: ServerVisibility; visibility_own: ServerVisibility; like_count: number; liked: boolean; images: number; parent: string | null; created_utc: string }>;
  /** `study` is the study's public id; `published` is false while the image converts or awaits its details. */
  recentUploads: Array<{ image_id: string; study: string; series_instance_uid: string; original_filename: string | null; description: string | null; kind: 'wsi' | 'flat' | 'document'; image_type: ImageType | null; study_id: string | null; published: boolean }>;
}

async function get<T>(path: string): Promise<T | null> {
  const res = await fetch(`${getConfig().apiRoot}${path}`, { credentials: 'include' });
  if (res.status === 404) return null;
  if (!res.ok) throw new HttpError(`Request failed (${res.status})`, res.status);
  return (await res.json()) as T;
}

const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** `guest` undefined = the session has not answered yet; nothing is asked until it has. */
export function useExplore(guest: boolean | undefined, o: { organ?: string; sort?: string; q?: string }) {
  return useQuery({
    queryKey: ['explore', guest, o],
    queryFn: () => get<ExploreData>(`${guest ? '/public' : ''}/explore${qs(o)}`),
    placeholderData: (prev) => prev,
    enabled: guest !== undefined,
  });
}

export function usePublicCollection(guest: boolean | undefined, publicId: string | undefined) {
  return useQuery({
    queryKey: ['public-collection', guest, publicId],
    queryFn: () => get<PublicCollection>(`${guest ? '/public/collections' : '/explore/collections'}/${encodeURIComponent(publicId!)}`),
    enabled: !!publicId && guest !== undefined,
  });
}

export function useSearch(q: string) {
  const { current } = useSelectedWorkspace();
  const ws = current?.public_id;
  return useQuery({
    queryKey: ['search', ws, q],
    queryFn: () => get<SearchResults>(`/search${qs({ q, workspace: ws })}`),
    enabled: q.trim().length > 0 && !!ws,
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}

export function useHome() {
  const { current, ready } = useSelectedWorkspace();
  const ws = current?.public_id;
  return useQuery({
    queryKey: ['home', ws],
    queryFn: () => get<HomeData>(`/home${qs({ workspace: ws })}`),
    enabled: ready && !!ws,
  });
}

/**
 * A guest's study covers come from the public namespace, by public id: a
 * Public study's first published image. Thumbnails are all it serves — a
 * guest gets no DICOMweb at all.
 */
export function publicThumbnail(studyId: string): string {
  if (import.meta.env.DEV && window.__TR_FIXTURES) return fixtureCover(studyId) ?? '#e8e8e6';
  return `${getConfig().apiRoot}/public/studies/${encodeURIComponent(studyId)}/thumbnail`;
}

/** One image's preview for a signed-in page (search results, Home), by public id. */
export function imageThumbnail(imageId: string): string {
  if (import.meta.env.DEV && window.__TR_FIXTURES) return fixtureCover(imageId) ?? '#e8e8e6';
  return `${getConfig().apiRoot}/images/${encodeURIComponent(imageId)}/thumbnail`;
}
